import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import partition from 'lodash/partition';
import pick from 'lodash/pick';
import {Component} from 'panel';

import {emitTrackEvent} from 'common/util/dom';
import {filterAttributes} from 'common/util';
import {pluralize} from 'common/util/string';
import {apiClient} from 'common/util/api-client';
import {currentProjectStore} from 'common/report/stores/project-details';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {getEntityTypePermissions, isSharingEnabledForProject} from 'common/report/util/saved-entities';
import {EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import {promptSharing} from 'common/widgets/sharing';

import {DisplayContext as PropertyFilterDisplayContext, ResourceType} from '../property-filter-menu/models/types';
import template from './custom-event-modal-internal.jade';
import css from './custom-event-modal-internal.styl?inline';
import {EventSelector, MPCustomEvent} from './models/events';
import {ELEMENTS_BY_CSS_CLASS, getResolvedCustomEvents} from './util/index';
import {deleteCustomEvent, saveCustomEvent} from './util/api';

const MAX_NAME_LENGTH = 255;
const DISPATCHED_EVENTS = {
  MODAL_CLOSED: `modalClosed`,
  CANCEL_EDITING: `cancelEditingCustomEvent`,
  DELETED: `deletedCustomEvent`,
  DELETING: `deletingCustomEvent`,
  SAVING: `savingCustomEvent`,
  SAVED: `savedCustomEvent`,
};

export class CustomEventModalInternal extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      events: `json`,
      'is-blocked-people': `boolean`,
      open: `boolean`,
      'project-has-people': `boolean`,
      'project-id': `string`,
      'selected-custom-event-id': `number`,
      'workspace-id': `number`,
      'all-data-groups': `boolean`,
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
      'track-name': `string`,
    };
  }
  constructor() {
    super();
    this.debouncedGetAffectedMessages = debounce(this.getAffectedMessages, 50);
  }

  static get dispatchedEvents() {
    return Object.values(DISPATCHED_EVENTS);
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    switch (attr) {
      case `is-blocked-people`:
        this.update({isBlockedPeople: this.attr(attr)});
        break;
      case `project-has-people`:
        this.update({projectHasPeople: this.attr(attr)});
        break;
      case `project-id`:
        this.initCurrentProjectStore();
        this.update({projectId: this.attr(attr)});
        break;
      case `events`: {
        const [customEvents, events] = partition(this.attr(attr), `custom`);
        this._customEvents = getResolvedCustomEvents(customEvents);
        this.update({events: [...events, ...this._customEvents]});
        this.updateStagedCustomEvent();
        break;
      }
      case `selected-custom-event-id`:
        if (this.attr(attr)) {
          this.updateStagedCustomEvent();
        } else {
          this.clearStagedCustomEvent();
        }
        break;
      case `open`: {
        const open = this.attr(attr);
        this.update({open});
        break;
      }
      default:
        break;
    }
    if (
      this.initialized &&
      this.attr(`selected-custom-event-id`) &&
      [`workspace-id`, `project-id`, `selected-custom-event-id`].includes(attr)
    ) {
      this.debouncedGetAffectedMessages(this.attr(`selected-custom-event-id`));
    }
  }

  initCurrentProjectStore() {
    const projectId = this.attr(`project-id`);
    if (projectId && !this.currentProjectStoreCallback) {
      this.currentProjectStoreCallback = () => this.update();
      currentProjectStore.subscribe(this.currentProjectStoreCallback).fetch({projectId});
    }
  }

  connectedCallback() {
    super.connectedCallback(...arguments);
    this.initCurrentProjectStore();
    this._userStoreCallback = () => this.update();
    this._parentPositions = {};
  }

  disconnectedCallback() {
    currentProjectStore.unsubscribe(this.currentProjectStoreCallback);
    super.disconnectedCallback();
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        [this.builderStateReference]: this.defaultBuilderState,
        error: {},
        open: false,
        showDeleteModal: false,
        stagedCustomEvent: new MPCustomEvent(),
      },
      helpers: {
        ELEMENTS_BY_CSS_CLASS,
        MAX_NAME_LENGTH,
        PropertyFilterDisplayContext,
        pick,
        pluralize,
        filterAttributes,
        getSharingBannerAttrs: () => ({
          'project-id': this.state.projectId,
          'entity-type': EntityType.CustomEvent,
        }),
        showShareManagement: () => {
          if (isSharingEnabledForProject(currentProjectStore.value)) {
            const entityTypePermissions = getEntityTypePermissions(this.attr(`project-id`), EntityType.CustomEvent);
            return entityTypePermissions.can_share;
          }
          return false;
        },
        handleModalChange: ({detail}) => {
          if (detail && detail.state === `closed`) {
            this.update({open: false});
            requestAnimationFrame(() => {
              this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.MODAL_CLOSED));
              // clear errors and other transient state when modal closes
              this.update(pick(this.config.defaultState, [`showDeleteModal`, `error`]));
            });
          }
        },
        handleErrorModalChange: (ev) => {
          if (ev.detail && ev.detail.state === `closed`) {
            this.update({error: {}});
          }
        },

        getEntries: () => {
          const {stagedCustomEvent} = this.state;
          return stagedCustomEvent.eventSelectors.map((eventSelector) => ({
            event: {
              custom: eventSelector.eventIsCustom(),
              label: eventSelector.getEventLabel(),
              value: eventSelector.getEventName(),
              ...(eventSelector.eventIsCustom()
                ? {
                    id: eventSelector.getEventId(),
                  }
                : {}),
            },
            filters: eventSelector.propertyFilters.map((propertyFilter) => propertyFilter.getAttributes()),
            filtersOperator: eventSelector.filtersOperator,
            type: ResourceType.Event,
          }));
        },

        updateQueryEvents: (ev) => {
          const {stagedCustomEvent} = this.state;
          stagedCustomEvent.eventSelectors = ev.detail.entries.map(
            ({filters, event, filtersOperator}) =>
              new EventSelector({
                propertyFilters: filters,
                event: event.custom ? {name: event.label, id: event.id} : event.value,
                filtersOperator,
              }),
          );
          this.update({stagedCustomEvent});
        },

        captureTracking: (ev) => {
          const {eventName, props} = ev.detail;
          ev.stopPropagation();
          this.track(eventName, props || {});
        },

        cancelChanges: () => {
          this.track(`Cancel custom event modal`);
          this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.CANCEL_EDITING));
        },
        isNewCustomEvent: () => !this.state.stagedCustomEvent.id,
        clearNameErrors: () => this.update({error: {}}),
        clickSave: ({andShare = false}) => {
          let {name, id} = this.state.stagedCustomEvent;
          name = name.trim();
          const allNames = this.customEvents
            .filter((customEvent) => customEvent.id !== id)
            .map((customEvent) => customEvent.name);
          if (!name) {
            this.update({error: {invalidName: true}});
          } else if (allNames.includes(name)) {
            this.update({error: {duplicateName: true}});
          } else {
            this.saveChanges({andShare});
          }
        },
        nameInputInserted: ({elm}) => {
          this.nameInputEl = elm;
        },
        updateCustomEventName: (ev) => {
          const stagedCustomEvent = this.state.stagedCustomEvent.updateName(ev.target.value);
          this.update({stagedCustomEvent});
        },
        clickDeleteCustomEvent: () => {
          this.update({showDeleteModal: true});
        },
        confirmDeleteCustomEvent: () => {
          this.deleteCustomEvent(this.state.stagedCustomEvent.id);
        },
        cancelDeleteCustomEvent: () => {
          this.update({showDeleteModal: false});
        },
      },
    };
  }

  async deleteCustomEvent(id) {
    const {projectId, affectedMessageIds} = this.state;
    this.track(`Delete custom event`);
    this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.DELETING));
    try {
      if (!isEmpty(affectedMessageIds)) {
        await this.pauseMessages(affectedMessageIds);
      }

      await deleteCustomEvent(id, {projectId, workspaceId: this.attr(`workspace-id`)});
      this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.DELETED, {detail: {id}}));
    } catch (err) {
      this.update({error: {deleteCustomEvent: true}, showDeleteModal: false, message: err.message});
      console.error(err);
    }
  }

  async saveChanges({andShare = false}) {
    const {stagedCustomEvent, projectId, affectedMessageIds} = this.state;
    const trackingEvent = stagedCustomEvent.id ? `Update custom event` : `Save new custom event`;
    this.track(trackingEvent);
    this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.SAVING));
    try {
      if (!isEmpty(affectedMessageIds)) {
        await this.pauseMessages(affectedMessageIds);
      }

      const result = await saveCustomEvent(stagedCustomEvent, {projectId, workspaceId: this.attr(`workspace-id`)});
      this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.SAVED, {detail: {customEvent: result.custom_event}}));
      this.clearStagedCustomEvent();
      if (andShare) {
        await promptSharing({
          type: SharableEntityType.CustomEvent,
          id: result.custom_event.id,
          'project-id': projectId,
        });
      }
    } catch (err) {
      this.update({error: {saveCustomEvent: true, message: err.message}});
      console.error(err);
    }
  }

  updateStagedCustomEvent() {
    const customEventId = this.attr(`selected-custom-event-id`);
    if (customEventId) {
      const selectedCustomEvent = this.customEvents.find((customEvent) => customEvent.id === customEventId);
      this.update({stagedCustomEvent: selectedCustomEvent});
    }
  }

  clearStagedCustomEvent() {
    this.update({stagedCustomEvent: new MPCustomEvent(), affectedMessageIds: []});
  }

  get customEvents() {
    return this._customEvents;
  }

  track(eventName, props = {}) {
    emitTrackEvent(this, {
      eventName,
      source: `mp-custom-event-modal`,
      props: {
        ...props,
        ...this.state.stagedCustomEvent.toTrackingData(),
      },
    });
  }

  //#region Builder methods

  get builderStateReference() {
    return `builder`;
  }

  get defaultBuilderState() {
    return {
      activeIndex: 0,
      panes: [],
      propertyFilterIndex: null,
      searchFilter: ``,
      selectorIndex: null,
    };
  }

  //#endregion

  //#region Notifications methods
  async pauseMessages(messageIds) {
    const {projectId} = this.state;
    const workspaceIdOrProjectId = this.attr(`workspace-id`)
      ? `workspace/${this.attr(`workspace-id`)}`
      : `${projectId}`;
    await apiClient.post(`/report/${workspaceIdOrProjectId}/triggers/pause/`, {
      trigger_ids: messageIds,
    });
  }

  getAffectedMessages(customEventId) {
    const {projectId} = this.state;
    const workspaceId = this.attr(`workspace-id`);
    const workspaceIdOrProjectId = workspaceId ? `workspaces/${workspaceId}` : `projects/${projectId}`;
    // using apiClient.fetch on legacy endpoint with incorrect format
    // eslint-disable-next-line local-rules/deprecated-member-access
    return apiClient
      .fetch(`/api/app/${workspaceIdOrProjectId}/custom_events/affected/${customEventId}/`)
      .then((response) => response.json())
      .then((response) => response.notif_ids_to_pause)
      .catch(() => []) // swallow errors
      .then((affectedMessageIds) => {
        this.update({affectedMessageIds});
      });
  }
  //#endregion
}

export const CustomEventModalInternalEl = defineAndMakeJsxFactory(
  `mp-custom-event-modal-internal`,
  CustomEventModalInternal,
);
